import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { authAPI } from './services/api';
import InsightsDashboard from './pages/InsightsDashboard';
import FieldTechnicianPortal from './pages/FieldTechnicianPortal';
import UserManagementPage from './pages/UserManagementPage';
import OperatorsPage from './pages/OperatorsPage';
import RobotsPage from './pages/RobotsPage';
import OEMHealthPortal from './pages/OEMHealthPortal';
import InternalPage from './pages/InternalPage';
import NovaAI from './pages/NovaAI';
import ElectricianDashboard from './pages/ElectricianDashboard';

// ─── Role Helpers ────────────────────────────────────────────────────────────

const REGIONAL_ROLES = ['regional', 'Regional', 'Regional ', 'Regional Admin'];
const SUPER_ADMIN_ROLES = ['super_admin', 'Superadmin', 'Super Admin'];
const ADMIN_ROLES    = [...SUPER_ADMIN_ROLES, 'company_admin', 'Admin', ...REGIONAL_ROLES];

const isGonxtUser = (user) => {
  const email = user?.email || user?.username || '';
  return email.toLowerCase().endsWith('@gonxt.tech');
};

const isRegionalRole = (role) => REGIONAL_ROLES.includes(role);
const isAdminRole    = (role) => ADMIN_ROLES.includes(role);

const getRoleDisplayLabel = (role) => {
  const map = {
    'super_admin':    'Super Admin',
    'Superadmin':     'Super Admin',
    'company_admin':  'Company Admin',
    'Admin':          'Admin',
    'regional':       'Regional Admin',
    'Regional':       'Regional Admin',
    'Regional ':      'Regional Admin',
    'Regional Admin': 'Regional Admin',
    'hospital_admin': 'Hospital Admin',
    'field_technician': 'Field Technician',
    'hospital_staff': 'Hospital Staff',
    'electrician': 'Electrician',
  };
  return map[role] || role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getRoleBadgeColor = (role) => {
  if (SUPER_ADMIN_ROLES.includes(role))
    return 'bg-purple-100 text-purple-700 border-purple-200';
  if (['company_admin', 'Admin'].includes(role))
    return 'bg-blue-100 text-blue-700 border-blue-200';
  if (REGIONAL_ROLES.includes(role))
    return 'bg-indigo-100 text-indigo-700 border-indigo-200';
  if (role === 'hospital_admin')
    return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (role === 'field_technician')
    return 'bg-amber-100 text-amber-700 border-amber-200';
  if (role === 'electrician')
    return 'bg-orange-100 text-orange-700 border-orange-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

// ─── Login Page ───────────────────────────────────────────────────────────────

const LoginPage = ({ onLogin }) => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await authAPI.login(email, password);
      onLogin(result.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-6 p-4 bg-white rounded-2xl shadow-sm border border-slate-100">
            <img src="/fybatex-logo.png" alt="Fybatex" className="h-14 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2 tracking-tight">EveSix Portal</h1>
          <p className="text-blue-600 text-base font-medium">UV Disinfection Management</p>
          <p className="text-slate-500 text-sm mt-1">Enterprise Portal v2.5</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-800 mb-1">Welcome Back</h2>
            <p className="text-slate-500 text-sm">Sign in to access your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
                <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-slate-50 text-slate-800 rounded-xl pl-12 pr-4 py-3.5 border border-slate-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder-slate-400"
                  placeholder="your.email@company.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-slate-50 text-slate-800 rounded-xl pl-12 pr-4 py-3.5 border border-slate-200 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder-slate-400"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${
                loading
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-slate-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="flex items-center justify-center space-x-2 text-sm text-slate-500">
              <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>Secure, encrypted connection</span>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">© 2025 EveSix Medical Technologies. All rights reserved.</p>
          <p className="text-slate-400 text-xs mt-1">ISO 13485 Certified | HIPAA Compliant</p>
        </div>
      </div>
    </div>
  );
};

// ─── Nav Link ─────────────────────────────────────────────────────────────────

const NavLink = ({ to, children, icon }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
        isActive
          ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/30'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`}
    >
      {icon}
      <span>{children}</span>
    </Link>
  );
};

// ─── App ──────────────────────────────────────────────────────────────────────

function App() {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const savedUser = authAPI.getCurrentUser();
        if (savedUser) {
          await authAPI.verifyToken();
          setUser(savedUser);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        authAPI.logout();
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogin  = (userData) => setUser(userData);
  const handleLogout = async () => {
    try { await authAPI.logout(); } finally { setUser(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center bg-white p-10 rounded-3xl shadow-xl border border-slate-100">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
            <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="text-slate-700 font-medium">Loading EveSix Portal...</p>
        </div>
      </div>
    );
  }

  if (!user) return <LoginPage onLogin={handleLogin} />;

  // ── Route helpers ──────────────────────────────────────────────────────────
  const getHomeRoute = () => {
    if (isAdminRole(user.role))         return '/company-dashboard';
    if (user.role === 'hospital_admin') return '/hospital-dashboard';
    if (user.role === 'hospital_staff') return '/hospital-dashboard';
    if (user.role === 'field_technician') return '/field-service';
    if (user.role === 'electrician') return '/electrician-dashboard';
    return '/';
  };

  // Icons used in nav
  const iconBuilding = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
  const iconGear = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
  const iconUsers = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
  const iconShield = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">

        {/* ── Nav ── */}
        <nav className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">

              {/* Left: logo + links */}
              <div className="flex items-center space-x-8">
                <Link to={getHomeRoute()} className="flex items-center space-x-3 group">
                  <div className="p-1.5 bg-slate-50 rounded-xl border border-slate-100">
                    <img src="/fybatex-logo.png" alt="Fybatex" className="h-8 w-auto object-contain" />
                  </div>
                  <div>
                    <span className="text-slate-800 font-bold text-lg">EveSix</span>
                    <span className="text-blue-600 text-xs block -mt-1">Medical Portal</span>
                  </div>
                </Link>

                <div className="hidden md:flex items-center space-x-2">
                  {/* Electrician gets a single-page experience — no nav links to other pages */}
                  {user.role === 'electrician' ? null : (
                  <>
                  {/* Admin / Regional → company dashboard */}
                  {isAdminRole(user.role) && (
                    <NavLink to="/company-dashboard" icon={iconBuilding}>Dashboard</NavLink>
                  )}

                  {/* Hospital roles */}
                  {(user.role === 'hospital_admin' || user.role === 'hospital_staff') && (
                    <NavLink to="/hospital-dashboard" icon={iconBuilding}>Hospital</NavLink>
                  )}

                  {/* Field technician */}
                  {user.role === 'field_technician' && (
                    <NavLink to="/field-service" icon={iconGear}>Field Service</NavLink>
                  )}

                  {/* Users page – super/company admin only */}
                  {(SUPER_ADMIN_ROLES.includes(user.role) || user.role === 'company_admin' || user.role === 'Admin') && (
                    <NavLink to="/users" icon={iconUsers}>Users</NavLink>
                  )}

                  {/* Operators – Super Admin only */}
                  {SUPER_ADMIN_ROLES.includes(user.role) && (
                    <NavLink to="/operators" icon={iconUsers}>Operators</NavLink>
                  )}

                  {/* Robots – Super Admin only */}
                  {SUPER_ADMIN_ROLES.includes(user.role) && (
                    <NavLink to="/robots" icon={iconGear}>Robots</NavLink>
                  )}

                  {/* Nova AI - available to all roles */}
                  <NavLink to="/nova-ai" icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  }>Nova AI</NavLink>

                  {/* GoNxt-only routes */}
                  {isGonxtUser(user) && (
                    <NavLink to="/oem-health" icon={iconShield}>OEM Health</NavLink>
                  )}
                  {isGonxtUser(user) && (
                    <NavLink to="/internal" icon={iconGear}>Internal</NavLink>
                  )}
                  </>
                  )}
                </div>
              </div>

              {/* Right: user info + logout */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <div className="text-right hidden sm:block">
                    <p className="text-slate-800 text-sm font-medium">
                      {user.first_name} {user.last_name}
                    </p>
                    {/* ✅ Fixed: uses getRoleDisplayLabel so Regional shows correctly */}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                      {getRoleDisplayLabel(user.role)}
                    </span>
                  </div>

                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </div>

                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 px-4 py-2 bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-600 rounded-xl transition-all duration-200 border border-slate-200 hover:border-red-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="hidden sm:inline text-sm font-medium">Logout</span>
                  </button>
                </div>
              </div>

            </div>
          </div>
        </nav>

        {/* ── Main ── */}
        <main className="flex-1">
          <Routes>
            <Route path="/"      element={<Navigate to={getHomeRoute()} />} />
            <Route path="/login" element={<Navigate to={getHomeRoute()} />} />

            {/* Company / Regional dashboard */}
            <Route
              path="/company-dashboard"
              element={
                isAdminRole(user.role) ? (
                  // ✅ Pass isRegional flag so InsightsDashboard can show hospital filter
                  <InsightsDashboard user={user} showHospitalFilter={isRegionalRole(user.role)} />
                ) : (
                  <Navigate to={getHomeRoute()} />
                )
              }
            />

            {/* Hospital dashboard */}
            <Route
              path="/hospital-dashboard"
              element={
                user.role === 'hospital_admin' || user.role === 'hospital_staff' ? (
                  <InsightsDashboard user={user} showHospitalFilter={false} />
                ) : (
                  <Navigate to={getHomeRoute()} />
                )
              }
            />

            {/* Field technician */}
            <Route
              path="/field-service"
              element={
                user.role === 'field_technician' ? (
                  <FieldTechnicianPortal
                    technicianId={user.user_id}
                    technicianName={`${user.first_name} ${user.last_name}`}
                  />
                ) : (
                  <Navigate to={getHomeRoute()} />
                )
              }
            />

            {/* Electrician – single-page fleet/emergency-stop dashboard */}
            <Route
              path="/electrician-dashboard"
              element={
                user.role === 'electrician' ? (
                  <ElectricianDashboard user={user} />
                ) : (
                  <Navigate to={getHomeRoute()} />
                )
              }
            />

            {/* User management */}
            <Route
              path="/users"
              element={
                [...SUPER_ADMIN_ROLES, 'company_admin', 'Admin', 'hospital_admin'].includes(user.role) ? (
                  <UserManagementPage user={user} />
                ) : (
                  <Navigate to={getHomeRoute()} />
                )
              }
            />

            {/* Robot operators – add operators without manual SQL (Super Admin only) */}
            <Route
              path="/operators"
              element={
                SUPER_ADMIN_ROLES.includes(user.role) ? (
                  <OperatorsPage user={user} />
                ) : (
                  <Navigate to={getHomeRoute()} />
                )
              }
            />

            {/* Robot fleet – register robots without manual SQL (Super Admin only) */}
            <Route
              path="/robots"
              element={
                SUPER_ADMIN_ROLES.includes(user.role) ? (
                  <RobotsPage user={user} />
                ) : (
                  <Navigate to={getHomeRoute()} />
                )
              }
            />

            {/* OEM Health – gonxt only */}
            <Route
              path="/oem-health"
              element={
                isGonxtUser(user) ? (
                  <OEMHealthPortal user={user} />
                ) : (
                  <Navigate to={getHomeRoute()} />
                )
              }
            />

            {/* Internal – gonxt only */}
            <Route
              path="/internal"
              element={
                isGonxtUser(user) ? (
                  <InternalPage user={user} />
                ) : (
                  <Navigate to={getHomeRoute()} />
                )
              }
            />

            {/* Nova AI – available to all authenticated users */}
            <Route
              path="/nova-ai"
              element={<NovaAI user={user} />}
            />
          </Routes>
        </main>

        {/* ── Footer ── */}
        <footer className="bg-white border-t border-slate-200 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-between text-sm text-slate-500">
              <p>© 2025 EveSix Medical Technologies. All rights reserved.</p>
              <div className="flex items-center space-x-4 mt-2 sm:mt-0">
                <span className="flex items-center space-x-1">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                  <span>System Online</span>
                </span>
                <span>v2.5.0</span>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </Router>
  );
}

export default App;